/**
 * Sweep trail — magenta ghost rect + edge line showing where void rose from.
 */

import type { Graphics } from 'pixi.js';
import { VOID } from '../../constants';
import type { VoidState } from './types';
import { visibleX } from './types';

export function updateSweepTrail(gfx: Graphics, dt: number, voidY: number, state: VoidState): void {
  gfx.clear();

  if (state.previousVoidY < 0) {
    state.previousVoidY = voidY;
    return;
  }

  // Detect rising (voidY decreasing = moving up)
  if (voidY < state.previousVoidY - 0.1) {
    state.sweepTrailAlpha = VOID.SWEEP_TRAIL_ALPHA;
    if (state.sweepTrailStartY < 0) {
      state.sweepTrailStartY = state.previousVoidY;
    }
  }

  // Draw sweep trail
  if (state.sweepTrailAlpha > 0.01 && state.sweepTrailStartY > 0) {
    const sv = visibleX();
    // Filled rect from current voidY down to where sweep began
    gfx.rect(sv.off, voidY, sv.w, state.sweepTrailStartY - voidY);
    gfx.fill({ color: VOID.EDGE_COLOR, alpha: state.sweepTrailAlpha * 0.3 });

    // Ghost edge polyline at sweepTrailStartY
    const segCount = VOID.EDGE_SEGMENTS;
    const segWidth = sv.w / segCount;
    // Use a simpler wave for the ghost (frozen at start position)
    gfx.moveTo(sv.off, state.sweepTrailStartY);
    for (let i = 1; i <= segCount; i++) {
      const x = sv.off + i * segWidth;
      const ghostY = state.sweepTrailStartY + Math.sin(x * 0.008) * 2;
      gfx.lineTo(x, ghostY);
    }
    gfx.stroke({ color: VOID.EDGE_COLOR, width: 1, alpha: state.sweepTrailAlpha * VOID.SWEEP_GHOST_EDGE_ALPHA });

    // Decay when not rising
    if (voidY >= state.previousVoidY - 0.1) {
      state.sweepTrailAlpha -= VOID.SWEEP_TRAIL_DECAY * dt;
      if (state.sweepTrailAlpha <= 0) {
        state.sweepTrailAlpha = 0;
        state.sweepTrailStartY = -1;
      }
    }
  }

  state.previousVoidY = voidY;
}
